import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createOrganization } from 'api/adminOrganizations';
import { queryKeys } from 'constants/queryKeys';
import { organizationManagementStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TCreateOrganizationRequest, TOrganization } from 'types';
import { toErrorMessage } from './utils';

type UseCreateOrganizationOptions = {
  onSuccess?: (organization: TOrganization) => void;
};

type UseCreateOrganizationResult = {
  create: (request: TCreateOrganizationRequest) => void;
  isSubmitting: boolean;
  error: string | undefined;
};

export function useCreateOrganization(
  options: UseCreateOrganizationOptions = {},
): UseCreateOrganizationResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = organizationManagementStrings.create;

  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: async (request: TCreateOrganizationRequest) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return createOrganization(request);
    },
    onSuccess: (organization) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ORGANIZATIONS, user?.uid],
      });
      setError(undefined);
      options.onSuccess?.(organization);
    },
    onError: (caught) => {
      setError(toErrorMessage(caught, strings.errors.submitFailed));
    },
  });

  const create = (request: TCreateOrganizationRequest) => {
    setError(undefined);
    mutation.mutate(request);
  };

  return {
    create,
    isSubmitting: mutation.isPending,
    error,
  };
}
